import { BookOpenTextIcon, CircleAlertIcon, ExternalLinkIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { openSidebar } from '@/redux/sidebar/actions';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

interface Props {
  providerName: string;
  providerUrl: string | undefined;
}

interface EmptyStateProps extends Props {
  hasEditableCategories: boolean;
}

const INLINE_LINK = [
  '[&>a]:text-sm [&>a]:leading-5 [&>a]:font-medium [&>a]:text-surface-button-primary',
  '[&>a]:underline [&>a]:decoration-solid [&>a]:decoration-auto [&>a]:underline-offset-auto',
  '[&>a]:[text-decoration-skip-ink:none] [&>a]:[text-underline-position:from-font]',
  '[&>a:hover]:text-surface-button-primary',
].join(' ');

const useEditArticles = () => {
  const dispatch = useDispatch();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );

  return {
    canWrite,
    editArticles: () => dispatch(openSidebar({ content: 'add_external_sources', meta: {} })),
  };
};

const VisitProviderLink = ({ providerName, providerUrl }: Props) => {
  const { t } = useTranslation();

  if (!providerUrl) {
    return null;
  }

  return (
    <Button
      asChild
      variant="ghost-neutral"
      size="sm"
      className="leading-6 hover:bg-transparent hover:underline"
    >
      <a href={providerUrl} target="_blank" rel="noopener noreferrer">
        <span className="flex items-start gap-0 px-1 text-foreground">
          {t('collections.external_source_issue.visit', { 0: providerName })}
        </span>
        <ExternalLinkIcon />
      </a>
    </Button>
  );
};

export const NoArticlesEmptyState = ({
  providerName,
  providerUrl,
  hasEditableCategories,
}: EmptyStateProps) => {
  const { t } = useTranslation();
  const { canWrite, editArticles } = useEditArticles();

  return (
    <Empty className="min-w-xs gap-4">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <BookOpenTextIcon aria-hidden />
        </EmptyMedia>
        <EmptyTitle>{t('collections.external_source_issue.empty_title')}</EmptyTitle>
        {hasEditableCategories ? (
          <EmptyDescription className="self-stretch">
            {t('collections.external_source_issue.empty_description')}
            <br />
            {t('collections.external_source_issue.resolve_hint', { 0: providerName })}
          </EmptyDescription>
        ) : (
          <EmptyDescription className={cn('self-stretch', INLINE_LINK)}>
            {t('collections.external_source_issue.empty_description')}
            <br />
            <Trans
              i18nKey="collections.external_source_issue.resolve_hint_link"
              values={{ 0: providerName }}
              components={{
                provider: providerUrl ? (
                  <a href={providerUrl} target="_blank" rel="noopener noreferrer">
                    {providerName}
                  </a>
                ) : (
                  <span>{providerName}</span>
                ),
              }}
            />
          </EmptyDescription>
        )}
      </EmptyHeader>
      {hasEditableCategories && (
        <EmptyContent className="gap-4">
          <Button size="sm" onClick={editArticles} disabled={!canWrite}>
            {t('collections.external_source_issue.edit_articles')}
          </Button>
          <VisitProviderLink providerName={providerName} providerUrl={providerUrl} />
        </EmptyContent>
      )}
    </Empty>
  );
};

export const MissingArticlesBanner = ({ providerName, providerUrl }: Props) => {
  const { t } = useTranslation();
  const { canWrite, editArticles } = useEditArticles();

  return (
    <Alert variant="info" className="mb-4 flex flex-col items-start gap-3 self-stretch p-4">
      <div className="flex items-start gap-2 self-stretch">
        <CircleAlertIcon className="size-4 shrink-0 text-informational" />
        <div className="flex flex-col items-start gap-0.5">
          <AlertTitle className="self-stretch">
            {t('collections.external_source_issue.missing_title')}
          </AlertTitle>
          <AlertDescription className="gap-0 self-stretch leading-6 font-normal text-foreground">
            <p className="leading-6">
              {t('collections.external_source_issue.missing_description')}
            </p>
            <p className="leading-6">
              {t('collections.external_source_issue.resolve_hint', { 0: providerName })}
            </p>
          </AlertDescription>
        </div>
      </div>
      <div className="flex items-center gap-2 self-stretch pl-6">
        <Button size="sm" onClick={editArticles} disabled={!canWrite}>
          {t('collections.external_source_issue.edit_articles')}
        </Button>
        <VisitProviderLink providerName={providerName} providerUrl={providerUrl} />
      </div>
    </Alert>
  );
};
